import React from 'react';
import { guestBarColor } from '../api.js';

/**
 * Horizontal speaking bar — supports 2-speaker and multi-speaker modes.
 *
 * Multi-speaker: pass `speakers` array [{ name, pct, color }, ...]
 * Two-speaker:   pass hostPct / guestPct / hostLabel / guestLabel / accentColor
 */
export default function SpeakingBar({
  // multi-speaker
  speakers,
  // two-speaker
  hostPct,
  guestPct,
  hostLabel = 'Host',
  guestLabel = 'Guest',
  accentColor,
}) {
  if (speakers && speakers.length > 0) {
    return <MultiBar speakers={speakers} />;
  }

  const host  = Math.max(0, Math.min(100, hostPct  ?? 0));
  const guest = Math.max(0, Math.min(100, guestPct ?? 0));

  const barStyle = accentColor
    ? {
        '--bar-host-color':  accentColor,
        '--bar-guest-color': guestBarColor(accentColor),
      }
    : {};

  return (
    <div style={barStyle}>
      <div
        className="speaking-bar"
        role="img"
        aria-label={`${hostLabel} ${host.toFixed(1)}%, ${guestLabel} ${guest.toFixed(1)}%`}
      >
        <div className="speaking-bar__host" style={{ width: `${host}%` }} />
        <div className="speaking-bar__guest" />
      </div>
      <div className="speaking-bar__labels" aria-hidden="true">
        <span className="speaking-bar__label--host">
          {hostLabel} {host.toFixed(1)}%
        </span>
        <span className="speaking-bar__label--guest">
          {guestLabel} {guest.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

function MultiBar({ speakers }) {
  const ariaLabel = speakers.map(s => `${s.name} ${s.pct.toFixed(1)}%`).join(', ');

  return (
    <div>
      <div className="speaking-bar speaking-bar--multi" role="img" aria-label={ariaLabel}>
        {speakers.map((s, i) => (
          <div
            key={s.name}
            className="speaking-bar__segment"
            style={{
              width: `${s.pct}%`,
              background: s.color,
              borderRadius:
                i === 0 ? '999px 0 0 999px'
                : i === speakers.length - 1 ? '0 999px 999px 0'
                : '0',
            }}
          />
        ))}
      </div>
      <div className="speaking-bar__labels--multi" aria-hidden="true">
        {speakers.map(s => (
          <span
            key={s.name}
            className="speaking-bar__label--multi"
            style={{ width: `${s.pct}%`, color: s.labelColor ?? s.color }}
            title={`${s.name} ${s.pct.toFixed(1)}%`}
          >
            <span className="speaking-bar__label-name">{s.name}</span>
            <span className="speaking-bar__label-pct">{s.pct.toFixed(1)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}
